import { normalizeLanguageScope } from './languageScope';

const FACTORS = [0.25, 0.5, 1, 2, 4];

export const emptyQueryWorkbench = (query = '', languageId = 'default') => ({
  id: null,
  revision: null,
  status: 'draft',
  languageId,
  name: '',
  note: '',
  queries: [query],
  rewrite: '',
  productChanges: {},
  groupChanges: { categories: {}, brands: {} },
  opaqueActions: [],
  validFrom: null,
  validTo: null,
});

const canonicalFactor = (value) => {
  const numeric = Number(value);
  return FACTORS.includes(numeric) ? numeric : 1;
};

const cloneAction = action => JSON.parse(JSON.stringify(action));

const editableResultGroupAction = (action) => {
  if (!['result_group.score', 'result_group.exclude'].includes(action.type)) return false;
  if (action.type === 'result_group.score' && !FACTORS.includes(Number(action.multiplier))) {
    return false;
  }
  if (action.target_field === 'brand_id') return action.include_descendants === undefined;
  if (action.target_field !== 'category_ids' || action.include_descendants !== true) return false;
  return true;
};

export const queryGroupToWorkbench = ({ group, rules } = {}, fallbackLanguageId = 'default') => {
  if (!group) return emptyQueryWorkbench('', fallbackLanguageId);
  const first = Array.isArray(rules) ? rules[0] : null;
  const productChanges = {};
  const groupChanges = { categories: {}, brands: {} };
  const opaqueActions = [];
  let rewrite = '';
  (first?.actions || []).forEach((action) => {
    if (action.type === 'query.rewrite') {
      rewrite = action.query || '';
      return;
    }
    if (action.type === 'product.hide') {
      (action.product_ids || []).forEach((id) => { productChanges[String(id)] = { TYPE: 'hidden' }; });
      return;
    }
    if (action.type === 'product.pin') {
      productChanges[String(action.product_id)] = {
        TYPE: 'pinned',
        POSITION: Number(action.position),
        ADMISSION: action.admission || 'force_include',
      };
      return;
    }
    if (editableResultGroupAction(action)) {
      const bucket = action.target_field === 'brand_id' ? 'brands' : 'categories';
      (action.target_ids || []).forEach((id) => {
        groupChanges[bucket][String(id)] = action.type === 'result_group.exclude'
          ? { TYPE: 'exclude' }
          : {
            TYPE: Number(action.multiplier) > 1 ? 'boost' : 'bury',
            FACTOR: canonicalFactor(action.multiplier),
          };
      });
      return;
    }
    opaqueActions.push(cloneAction(action));
  });

  return {
    id: String(group.id),
    revision: Number(group.revision),
    status: group.status || first?.status || 'draft',
    languageId: normalizeLanguageScope(
      group.language_id ?? first?.language_id,
      fallbackLanguageId,
    ),
    name: group.name || '',
    note: group.note || '',
    queries: (group.members || []).map((member) => member.query),
    rewrite,
    productChanges,
    groupChanges,
    opaqueActions,
    validFrom: first?.valid_from || null,
    validTo: first?.valid_to || null,
  };
};

const groupedTargetActions = (groupChanges = {}) => {
  const grouped = new Map();
  ['categories', 'brands'].forEach((bucket) => {
    Object.entries(groupChanges[bucket] || {}).forEach(([id, effect]) => {
      if (!effect) return;
      const exclude = effect.TYPE === 'exclude';
      const factor = canonicalFactor(effect.FACTOR);
      if (!exclude && factor === 1) return;
      const type = exclude ? 'result_group.exclude' : 'result_group.score';
      const targetField = bucket === 'brands' ? 'brand_id' : 'category_ids';
      const includeDescendants = bucket === 'categories';
      const key = `${type}:${targetField}:${factor}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          type,
          target_field: targetField,
          target_ids: [],
          ...(exclude ? {} : { multiplier: factor }),
          ...(includeDescendants ? { include_descendants: true } : {}),
        });
      }
      grouped.get(key).target_ids.push(String(id));
    });
  });
  return [...grouped.values()].map((action) => ({
    ...action,
    target_ids: action.target_ids.sort((left, right) => left.localeCompare(right, undefined, { numeric: true })),
  }));
};

export const queryWorkbenchActions = (model) => {
  const actions = [];
  if (model.rewrite?.trim()) actions.push({ type: 'query.rewrite', query: model.rewrite.trim() });
  const hidden = [];
  const pins = [];
  Object.entries(model.productChanges || {}).forEach(([id, change]) => {
    if (change?.TYPE === 'hidden') hidden.push(String(id));
    if (change?.TYPE === 'pinned' || change?.TYPE === 'added') {
      pins.push({
        type: 'product.pin',
        product_id: String(id),
        position: Number(change.POSITION),
        admission: change.ADMISSION || 'force_include',
      });
    }
  });
  pins.sort((left, right) => left.position - right.position || left.product_id.localeCompare(right.product_id));
  actions.push(...pins);
  if (hidden.length) {
    actions.push({
      type: 'product.hide',
      product_ids: hidden.sort((left, right) => left.localeCompare(right, undefined, { numeric: true })),
    });
  }
  actions.push(...groupedTargetActions(model.groupChanges));
  actions.push(...(model.opaqueActions || []).map(cloneAction));
  return actions;
};

export const queryWorkbenchSharedRule = (model) => ({
  conditions: { match: 'all', items: [] },
  actions: queryWorkbenchActions(model),
  valid_from: model.validFrom || null,
  valid_to: model.validTo || null,
});

export const queryWorkbenchPreviewRule = (model, query) => ({
  phase: 'query',
  kind: 'query_override',
  language_scope: 'language',
  trigger: { display_value: query },
  ...queryWorkbenchSharedRule(model),
});

export const previewProductCard = (product = {}) => ({
  ID: String(product.id),
  PRODUCT_NAME: product.name || `Product #${product.id}`,
  IMAGE: product.image_url
    || product.thumbnail_url
    || product.thumbnail
    || product.picture
    || product.image
    || '',
  SKU: product.reference || `#${product.id}`,
  PRICE: product.price == null ? '' : String(product.price),
  MATERIAL: product.vendor || '',
  BRAND: product.vendor || '',
  CATEGORY: product.category_name || product.categoryName || '',
  MEMBERSHIPS: product.group_memberships || product.merchandising_group_memberships || {},
  POSITION: Number(product.position) || null,
});

export const previewFacetItem = (group = {}) => ({
  ID: String(group.id),
  NAME: group.name || `#${group.id}`,
  COUNT: Number(group.count) || 0,
  PATH: group.path || null,
});
